import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface InteractionEvent {
  type: 'click' | 'scroll' | 'input' | 'navigation' | 'cart_action';
  target?: string;
  timestamp: string;
  details?: Record<string, any>;
}

interface SessionTrackingOptions {
  sessionId: string;
  userId?: string;
  onSave?: () => void;
}

export const useSessionTracking = ({ sessionId, userId, onSave }: SessionTrackingOptions) => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const interactionsRef = useRef<InteractionEvent[]>([]);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const analyticsIdRef = useRef<string | null>(null);

  // Initialize or update session analytics
  const initializeAnalytics = useCallback(async () => {
    if (!sessionId) return;

    try {
      const pageRoute = location.pathname;
      
      // Check if analytics record exists for this session and page
      const { data: existing } = await supabase
        .from('session_analytics')
        .select('id')
        .eq('session_id', sessionId)
        .eq('page_route', pageRoute)
        .maybeSingle();

      if (existing) {
        analyticsIdRef.current = existing.id;
      } else {
        // Get client location and device info
        let locationData = {
          country_code: null,
          country_name: null,
          city: null,
          latitude: null,
          longitude: null,
        };

        try {
          const { data: locData } = await supabase.functions.invoke('get-client-location');
          if (locData?.success) {
            locationData = {
              country_code: locData.country_code,
              country_name: locData.country_name,
              city: locData.city,
              latitude: locData.latitude,
              longitude: locData.longitude,
            };
          }
        } catch (err) {
          console.warn('Could not fetch location:', err);
        }

        // Get device and browser info
        const userAgent = navigator.userAgent;
        const deviceType = /Mobile|Android|iPhone/i.test(userAgent) ? 'mobile' : 'desktop';
        const browser = userAgent.includes('Chrome') ? 'Chrome' :
                       userAgent.includes('Firefox') ? 'Firefox' :
                       userAgent.includes('Safari') ? 'Safari' : 'Other';
        const os = userAgent.includes('Windows') ? 'Windows' :
                  userAgent.includes('Mac') ? 'macOS' :
                  userAgent.includes('Linux') ? 'Linux' :
                  userAgent.includes('Android') ? 'Android' :
                  userAgent.includes('iOS') ? 'iOS' : 'Other';

        // Create new analytics record
        const { data: newRecord } = await supabase
          .from('session_analytics')
          .insert({
            session_id: sessionId,
            user_id: userId || null,
            page_route: pageRoute,
            time_spent_seconds: 0,
            interactions: [],
            abandoned_cart: false,
            ...locationData,
            device_type: deviceType,
            browser: browser,
            os: os,
          })
          .select('id')
          .single();

        if (newRecord) {
          analyticsIdRef.current = newRecord.id;
        }
      }
    } catch (error) {
      console.error('Error initializing analytics:', error);
    }
  }, [sessionId, userId, location.pathname]);

  // Save analytics to database
  const saveAnalytics = useCallback(async (isPageUnload = false) => {
    if (!analyticsIdRef.current || !sessionId) return;

    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);

    try {
      const updateData: any = {
        time_spent_seconds: timeSpent,
        interactions: interactionsRef.current,
        updated_at: new Date().toISOString(),
      };

      // On page unload, mark potential cart abandonment
      if (isPageUnload && location.pathname === '/shop') {
        updateData.abandoned_cart = true;
      }

      await supabase
        .from('session_analytics')
        .update(updateData)
        .eq('id', analyticsIdRef.current);

      onSave?.();
    } catch (error) {
      console.error('Error saving analytics:', error);
    }
  }, [sessionId, location.pathname, onSave]);

  // Track interaction
  const trackInteraction = useCallback((
    type: InteractionEvent['type'],
    target?: string,
    details?: Record<string, any>
  ) => {
    const interaction: InteractionEvent = {
      type,
      target,
      timestamp: new Date().toISOString(),
      details,
    };

    interactionsRef.current.push(interaction);

    // Debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveAnalytics();
    }, 2000);
  }, [saveAnalytics]);

  // Track cart updates
  const trackCartUpdate = useCallback(async (cartData: any) => {
    if (!sessionId) return;

    try {
      // Update cart_data in session_analytics
      if (analyticsIdRef.current) {
        await supabase
          .from('session_analytics')
          .update({
            cart_data: cartData,
            abandoned_cart: false, // Reset abandonment flag when cart is updated
          })
          .eq('id', analyticsIdRef.current);
      }


      trackInteraction('cart_action', 'cart_update', { itemCount: cartData.length });
    } catch (error) {
      console.error('Error tracking cart update:', error);
    }
  }, [sessionId, trackInteraction]);

  // Initialize on mount and route change
  useEffect(() => {
    startTimeRef.current = Date.now();
    interactionsRef.current = [];
    initializeAnalytics();

    return () => {
      // Save on unmount
      saveAnalytics(true);
    };
  }, [location.pathname, initializeAnalytics]);

  // Save periodically
  useEffect(() => {
    const interval = setInterval(() => {
      saveAnalytics();
    }, 30000); // Save every 30 seconds

    return () => clearInterval(interval);
  }, [saveAnalytics]);

  // Track clicks globally
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const elementInfo = target.getAttribute('data-track') || 
                         target.tagName.toLowerCase() + 
                         (target.id ? `#${target.id}` : '') +
                         (target.className ? `.${target.className.split(' ')[0]}` : '');
      
      trackInteraction('click', elementInfo);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [trackInteraction]);

  // Track page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveAnalytics(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [saveAnalytics]);

  return {
    trackInteraction,
    trackCartUpdate,
    saveAnalytics,
  };
};
